// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Button, Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import { useTheme } from "@mui/system";
import { ErrorDialog } from "Components/ErrorDialog";
import downloadIcon from "Assets/download-icon.png";
import { GetAdminProjectRoute } from "Utils/routes";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "Hooks";
import { useEffect, useState } from "react";
import { RemoveEstimatesThunk } from "Features/admin/adminThunk";

import stylesDef from "./styles";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PDF from "Features/order/PDF";
import { GetPdfDataThunk } from "Features/order/orderThunk";

interface IErrorPricePerCreditDialogProps {
  showErrorPricePerCreditDialog: boolean;
  onClose: () => void;
  projects: {
    orderId: string;
    project: IProject;
  }[];
}

const ErrorPricePerCreditDialog = ({
  showErrorPricePerCreditDialog,
  projects,
  onClose,
}: IErrorPricePerCreditDialogProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const navigate = useNavigate();
  const { org } = useAppSelector((state) => state.admin);
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [pdfData, setPdfData] = useState<any[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchPdfData = async (orderId: string) => {
    setPdfLoading(true);
    try {
      const result = await dispatch(GetPdfDataThunk(orderId));
      if (result.success) {
        setPdfData((prev) => [...prev, result.data.pdfData]);
      }
    } finally {
      setPdfLoading(false);
    }
  };

  const handleViewProject = (projectId: string) => {
    navigate(GetAdminProjectRoute(org._id, projectId));
  };

  const handleClearAllEstimates = async () => {
    setLoading(true);
    const result = await dispatch(
      RemoveEstimatesThunk(org._id, projects.map((project) => project.orderId))
    );
    if (result.success) {
      onClose();
    }
    setLoading(false);
  };

  useEffect(() => {
    if (showErrorPricePerCreditDialog) {
      projects.forEach((project) => fetchPdfData(project.orderId));
    }

    return () => {
      setPdfData([]);
    };
  }, [showErrorPricePerCreditDialog, projects]);

  return (
    <ErrorDialog
      showDialog={showErrorPricePerCreditDialog}
      error={true}
      titleText="Error updating price per credit"
      cancelText="CLEAR ALL ESTIMATES"
      acceptText="CLOSE"
      cancelButton={
        <Button
          variant="contained"
          color="primary"
          onClick={handleClearAllEstimates}
          disabled={loading}
          sx={styles.clearAllEstimatesButton}
        >
          {loading ? <CircularProgress size={24} /> : "CLEAR ALL ESTIMATES"}
        </Button>
      }
      acceptButton={
        <Button variant="text" sx={styles.closeButton} onClick={onClose}>
          CLOSE
        </Button>
      }
    >
      <Box sx={styles.errorDialogContent}>
        <Typography variant="body1" sx={styles.errorDialogContentText}>
          We've found the following unsubmitted estimates causing this error:
        </Typography>
        {projects.map((estimate, index) => {
          return (
            <Box key={estimate.orderId} sx={styles.errorDialogContentItem}>
              <Tooltip title={estimate.project.name} arrow placement="top">
                <Typography
                  variant="body1"
                  sx={styles.errorDialogContentItemTitle}
                >
                  {estimate.project.name}
                </Typography>
              </Tooltip>
              {pdfLoading && pdfData.length < projects.length && (
                <Box sx={styles.downloadIcon}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!pdfLoading && pdfData.length === projects.length && (
                <PDFDownloadLink
                  id="pdf-download-link"
                  document={<PDF data={pdfData[index]} />}
                  fileName={`${estimate.project.name}_estimate.pdf`}
                >
                  <Box sx={styles.downloadIcon}>
                    <img src={downloadIcon} alt="download" />
                  </Box>
                </PDFDownloadLink>
              )}
              <Button
                variant="text"
                color="primary"
                onClick={() => handleViewProject(estimate.project._id)}
              >
                View
              </Button>
            </Box>
          );
        })}
      </Box>
    </ErrorDialog>
  );
};

export default ErrorPricePerCreditDialog;
